import React, { memo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { ConfigProvider, Spin } from 'antd';

import styled from 'styled-components';
import { mobile, tablet } from 'services/ui';
import { careerAccentVars } from 'services/careerAccents';
import { careerColor, careerTokenVars, TONE_DARK, TONE_LIGHT } from 'services/careerTokens';
import { useThemeMode } from 'context/theme';
import { careerDesktop, careerLayout, careerShellPadding, careerTablet } from 'services/careerType';
import { PATHS } from 'router/config';
import { useCareerJourney, useCareerRoleGuard, useMyCareerCenter } from 'core';
import { CareerPreviewNotice, CareerSectionNav } from 'molecules';
import { CareerApplyPrompt } from 'organisms';

import { CareerPlanSkeleton } from './parts/CareerPlanSkeleton';

interface ICareerLayout {
  children: ReactNode;
}

/**
 * Рамка карьерного раздела (ТЗ 0069) — общая для всех восьми экранов `/career/*`.
 *
 * Что она забирает у страниц и почему:
 *
 * * **Ролевой гард.** Один вызов вместо четырёх копий одного `useEffect`.
 *   Преподаватель и демо-пользователь получают 404: раздел им не показывают, и
 *   сервер отбивает их токен по всем карьерным полям.
 * * **Плашка предпросмотра.** Раньше она вставлялась руками в семь файлов, и
 *   правило «обязательна на КАЖДОМ экране» держалось на дисциплине. Здесь оно
 *   становится свойством кода: новый экран получает плашку самим фактом того,
 *   что живёт под `/career`.
 * * **Под-навигация.** Восемь экранов под одним пунктом меню.
 *
 * 🔴 Гейта ЧЛЕНСТВА здесь нет и не будет. Он переехал на разделы: не-участник
 * остаётся на странице и читает объяснение с одним CTA (`myCareerJourney.locked`),
 * а не получает 404 на продающем экране. Раньше оба слоя были свалены в
 * `useCareerAccessGuard`, и `/career` был невидим ровно той аудитории, ради
 * которой делается.
 */
export const CareerLayout = memo(({ children }: ICareerLayout) => {
  const { isPreview, center } = useMyCareerCenter();
  // 🔴 Маршрут монтируется ЗДЕСЬ, и это несущее свойство, а не оптимизация.
  //
  // `refetchQueries: ['CareerJourneyBoard']` сопоставляется с именем операции и
  // молча ничего не делает, если запрос в этот момент не смонтирован. Раньше
  // его монтировали сами страницы, и на `/career/professions` — единственном
  // месте, откуда выбирают карьерную цель, — его не было: студент выбирал цель
  // и возвращался на карту с прежней целью и прежним следующим действием. Ни
  // один гейт этого не видел, потому что комментарий про «он смонтирован в
  // CareerLayout» стоял в четырёх файлах, а вызова не было ни в одном.
  const { journey, loading } = useCareerJourney();

  useCareerRoleGuard();

  /**
   * 0072, выравнивание: antd внутри раздела говорит токенами раздела. Синие
   * чекбоксы, переключатели и фокус полей ввода по умолчанию antd спорили с
   * нейтральной палитрой — меняем только здесь, остальной LMS не затронут.
   * Вложенный `ConfigProvider` наследует алгоритм тёмной темы от корневого.
   */
  const { resolvedTheme } = useThemeMode();
  const tone = resolvedTheme === 'dark' ? TONE_DARK : TONE_LIGHT;
  const antdTheme = {
    token: {
      colorPrimary: tone.text,
      colorLink: tone.text,
      colorLinkHover: tone.text2,
      // Текст на «основной» кнопке antd: в тёмной теме основной цвет светлый,
      // и белая надпись на нём не читалась бы.
      colorTextLightSolid: resolvedTheme === 'dark' ? TONE_DARK.bg : '#FFFFFF',
      borderRadius: careerLayout.controlRadius,
      fontFamily: 'Rubik, sans-serif',
    },
  };

  const { pathname } = useLocation();
  const onPlan = pathname === PATHS.careerCenter || pathname === `${PATHS.careerCenter}/`;

  // Ждём ТОЛЬКО первую загрузку — по приехавшим данным, а не по `loading`.
  // Прежнее условие смотрело на `myCareerCenter === null`, который у
  // НЕ-УЧАСТНИКА null всегда, и при `cache-and-network` он получал белый экран
  // со спиннером на каждый клик по под-навигации. Он и есть целевая аудитория
  // витрины.
  //
  // «Визуальное упрощение» (15.09): оболочка с меню рисуется сразу, ждёт только
  // содержимое — на главной скелетом той же формы, чтобы страница не прыгала.
  const firstLoad = loading && journey === null;

  return (
    // 🔴 `width: 100%` обязателен: `ContentWrapper` шаблона — это flex-колонка
    // с `align-items: flex-start`, и без ширины раздел схлопывается по
    // содержимому. Ни tsc, ни сборка, ни тесты этого не видят — только глаза.
    // `data-career-section` включает видимый фокус на весь раздел (src/index.css).
    // Глобального :focus-стиля в проекте нет вовсе, а вводить его сразу на весь
    // ЛМС значит менять поведение каждого экрана одной строкой.
    <ConfigProvider theme={antdTheme}>
      <Wrapper data-career-section>
        {isPreview ? <CareerPreviewNotice /> : null}
        <Grid>
          {/* Доработка 0071 (15.09): боковое меню со всеми экранами раздела.
            Счётчики — из уже смонтированных запросов, без новых. Ширина меню
            постоянная на всех экранах, карты навыков в том числе. */}
          <CareerSectionNav
            badges={{
              lessons:
                center && center.lessonsTotal > 0
                  ? `${center.lessonsCompleted}/${center.lessonsTotal}`
                  : undefined,
              applications: journey?.applications.active || undefined,
              locked: journey?.locked.map((row) => row.key),
            }}
          />
          <Content data-career-content>
            {firstLoad ? (
              onPlan ? (
                <CareerPlanSkeleton />
              ) : (
                <Center>
                  <Spin size="large" />
                </Center>
              )
            ) : (
              <>
                {/* Вопрос «вы отправили заявку?» живёт в РАМКЕ, а не на ленте вакансий:
                  человек мог вернуться с сайта работодателя на любой экран раздела,
                  и спрашивать его только на той странице, с которой он ушёл, значит
                  не спросить вовсе. */}
                <CareerApplyPrompt />
                {children}
              </>
            )}
          </Content>
        </Grid>
      </Wrapper>
    </ConfigProvider>
  );
});

const Wrapper = styled.div`
  ${careerTokenVars}
  ${careerAccentVars}
  box-sizing: border-box;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-bottom: 80px;
  background: ${careerColor.bg};
  color: ${careerColor.text};
  ${careerShellPadding}

  ${tablet} {
    padding-bottom: 60px;
  }

  ${mobile} {
    padding-bottom: 48px;
  }
`;

/**
 * Меню слева, содержимое справа (ТЗ «визуальное упрощение», 2.1–2.3):
 * desktop 224 + 40, планшет 200 + 24, телефон — одна колонка с кнопкой меню.
 */
const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  padding-top: 24px;

  ${careerTablet} {
    grid-template-columns: ${careerLayout.shellMenuTablet}px minmax(0, 1fr);
    gap: ${careerLayout.shellGapTablet}px;
  }

  ${careerDesktop} {
    grid-template-columns: ${careerLayout.shellMenu}px minmax(0, 1fr);
    gap: ${careerLayout.shellGap}px;
  }
`;

const Content = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const Center = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 80px 0;
`;
